/**
 * Operaciones — global cockpit (PR-6 builder reorg).
 *
 * One read-only aggregate call (`operationsApi.overview()`) renders:
 *   - Scheduler card: enabled/running + lease holder + last tick +
 *     the process-level kill-switch (POST /scheduler/control).
 *   - Per-project schedule table: cron/tz/mode/enabled + rolling state
 *     (last run, next run, consecutive failures) + actions: Run now,
 *     Force-reap (only when a job is in flight — aborts it first),
 *     deep-link to the project hub's Auto-refresh tab.
 *   - Cross-project recent runs: status + live phase (progress_json)
 *     + duration + reconciliation chip (gate/portal semantics, same
 *     two shapes the hub badge reads) + error summary.
 *
 *   - Data-scope build health (Plan J J6, `ScopesPanel`): one row per
 *     (project, scope) with the last build's status, whether it was cut
 *     from the project file that is live now, its bytes on disk and its
 *     measured lock hold. Its own call, because every scope endpoint
 *     but that one is gated `manage_users` while this page is gated
 *     `operations`.
 *
 * Detail surfaces (job history pagination, recon drawer) stay in the
 * hub — the cockpit links there instead of duplicating them.
 * Auto-refreshes every 30 s while any job is pending/running.
 */

import { Badge, Button, Card, CardBody } from "@omnitok/ui";
import {
  AlertCircle,
  ArrowUpRight,
  Check,
  Pause,
  Play,
  RefreshCw,
  Skull,
  Zap,
} from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useT } from "@/i18n";
import { PageContainer, PageHeader } from "@pervasivemind/admin-ui";
import {
  deliveryApi,
  operationsApi,
  projectsApi,
  schedulerApi,
  type DeliveryJob,
  type LoaderJob,
  type OperationsOverviewResponse,
} from "@/lib/builder-api";
import { parseProjectName } from "@/lib/project-name";

import { ScopesPanel } from "./ScopesPanel";
import {
  resolveLocalized,
  type DisplayLabel,
} from "@pervasivemind/dashboard-render";

export function OperationsPage(): JSX.Element {
  const { t, locale } = useT();
  const [data, setData] = useState<OperationsOverviewResponse | null>(null);
  // Track B2 — cross-project delivery history. Fetched alongside the
  // overview so the cockpit shows loader runs AND customer sends.
  const [deliveries, setDeliveries] = useState<DeliveryJob[]>([]);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [togglingScheduler, setTogglingScheduler] = useState(false);

  const showFeedback = useCallback((msg: string): void => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  }, []);

  const load = useCallback(async (background = false): Promise<void> => {
    if (!background) setLoading(true);
    try {
      const [overview, recentDeliveries] = await Promise.all([
        operationsApi.overview(),
        deliveryApi.recentJobs(20),
      ]);
      setData(overview);
      setDeliveries(recentDeliveries.jobs);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Live refresh while something is running — the cockpit's whole
  // point is watching corridas vivas.
  const hasLiveJob = useMemo(
    () =>
      (data?.recent_jobs ?? []).some(
        (j) => j.status === "running" || j.status === "pending",
      ) ||
      deliveries.some(
        (j) => j.status === "running" || j.status === "pending",
      ),
    [data, deliveries],
  );
  useEffect(() => {
    if (!hasLiveJob) return;
    const id = setInterval(() => void load(true), 30_000);
    return () => clearInterval(id);
  }, [hasLiveJob, load]);

  const handleSchedulerToggle = async (): Promise<void> => {
    if (!data) return;
    const next = !data.scheduler.enabled;
    if (
      !next &&
      !window.confirm(t("operations.scheduler.confirmDisable"))
    ) {
      return;
    }
    setTogglingScheduler(true);
    try {
      const scheduler = await schedulerApi.control(next);
      setData({ ...data, scheduler });
      showFeedback(t("common.saved"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setTogglingScheduler(false);
    }
  };

  const handleRunNow = async (
    slug: string,
    forceReap: boolean,
  ): Promise<void> => {
    if (
      forceReap &&
      !window.confirm(t("operations.confirmForceReap", { slug }))
    ) {
      return;
    }
    setBusySlug(slug);
    try {
      await projectsApi.loader.runNow(slug, { forceReap });
      showFeedback(t("operations.runStarted", { slug }));
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setBusySlug(null);
    }
  };

  const handleRetryDelivery = async (jobId: string): Promise<void> => {
    setRetryingJobId(jobId);
    try {
      // Same row, same id — receivers dedup on X-BI-Delivery-Id, so a
      // retry must never mint a new job.
      await deliveryApi.retryJob(jobId);
      showFeedback(t("delivery.action.retry"));
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setRetryingJobId(null);
    }
  };

  const projectLabel = useCallback(
    (slug: string): string => {
      const p = data?.projects.find((x) => x.slug === slug);
      if (!p) return slug;
      return (
        resolveLocalized(parseProjectName(p.name) as DisplayLabel, locale) ||
        slug
      );
    },
    [data, locale],
  );

  if (loading) {
    return (
      <div className="flex h-[70vh] items-center justify-center text-sm text-neutral-500">
        <Spinner size={16} className="mr-2" ariaLabel={t("common.loading")} />
        {t("common.loading")}
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("operations.title")}
        subtitle={t("operations.subtitle")}
        actions={
          <Button
            ghost
            size="sm"
            onClick={() => void load()}
            leftIcon={<RefreshCw size={14} />}
          >
            {t("common.refresh")}
          </Button>
        }
      />

      {feedback && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-4 py-2 text-sm text-success-dark">
          <Check size={14} /> {feedback}
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertCircle size={14} /> {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-error-dark"
            aria-label={t("common.cancel")}
          >
            ✕
          </button>
        </div>
      )}

      {data && (
        <div className="space-y-5">
          {/* ── Scheduler global ─────────────────────────────── */}
          <Card variant="bordered">
            <CardBody>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-2">
                  {/* THREE states, and the third one used to be visible
                      ONLY as a colour. The dot went green / amber / red
                      for running / enabled-but-no-leader / paused while
                      the Badge beside it reported just enabled-vs-
                      disabled — so a red-green colour-blind reader (the
                      owner of this product is one) could not tell a
                      scheduler that is running from one that is enabled
                      and has no leader, which is precisely the state
                      somebody opens this page to find. The glyph and
                      the word carry it now; the dot is decoration. */}
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      data.scheduler.enabled && data.scheduler.running
                        ? "bg-success"
                        : data.scheduler.enabled
                          ? "bg-warning"
                          : "bg-error"
                    }`}
                    aria-hidden
                  />
                  <span className="text-sm font-semibold text-neutral-900">
                    {t("operations.scheduler.title")}
                  </span>
                  <Badge
                    variant={
                      data.scheduler.enabled && data.scheduler.running
                        ? "success"
                        : data.scheduler.enabled
                          ? "warning"
                          : "error"
                    }
                    size="sm"
                    pill
                  >
                    {data.scheduler.enabled && data.scheduler.running
                      ? `▶ ${t("operations.scheduler.running")}`
                      : data.scheduler.enabled
                        ? `⚠ ${t("operations.scheduler.noLeader")}`
                        : `⏸ ${t("operations.scheduler.paused")}`}
                  </Badge>
                </div>
                <span className="text-xs text-neutral-500">
                  {t("operations.scheduler.lease")}:{" "}
                  <span className="font-mono">
                    {data.scheduler.lease_holder ?? "—"}
                  </span>
                </span>
                <span className="text-xs text-neutral-500">
                  {t("operations.scheduler.lastTick")}:{" "}
                  {formatTs(data.scheduler.last_tick_at)}
                </span>
                <div className="ml-auto">
                  <Button
                    variant={data.scheduler.enabled ? "danger" : "primary"}
                    ghost={data.scheduler.enabled}
                    size="xs"
                    onClick={() => void handleSchedulerToggle()}
                    disabled={togglingScheduler}
                    loading={togglingScheduler}
                    leftIcon={
                      data.scheduler.enabled ? (
                        <Pause size={12} />
                      ) : (
                        <Play size={12} />
                      )
                    }
                  >
                    {data.scheduler.enabled
                      ? t("operations.scheduler.pause")
                      : t("operations.scheduler.resume")}
                  </Button>
                </div>
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {t("operations.scheduler.note")}
              </p>
            </CardBody>
          </Card>

          {/* ── Programación por proyecto ────────────────────── */}
          <Card variant="bordered" noPadding>
            <header className="border-b border-neutral-200 bg-neutral-50 px-4 py-2">
              <span className="text-sm font-semibold text-neutral-800">
                {t("operations.projects.title")}
              </span>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-neutral-200 bg-neutral-50/60">
                  <tr>
                    <Th>{t("operations.projects.project")}</Th>
                    <Th>{t("operations.projects.schedule")}</Th>
                    <Th>{t("operations.projects.lastRun")}</Th>
                    <Th>{t("operations.projects.nextRun")}</Th>
                    <Th className="text-right">
                      {t("adminUsers.table.actions")}
                    </Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {data.projects.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-6 text-center text-neutral-500"
                      >
                        {t("operations.projects.empty")}
                      </td>
                    </tr>
                  ) : (
                    data.projects.map((p) => {
                      const inFlight = Boolean(p.state.current_job_id);
                      return (
                        <tr key={p.slug} className="hover:bg-neutral-50">
                          <td className="px-4 py-3">
                            <Link
                              to={`/projects/${encodeURIComponent(p.slug)}?tab=loader`}
                              className="font-medium text-neutral-900 hover:text-primary"
                            >
                              {projectLabel(p.slug)}
                            </Link>
                            <span className="ml-2 font-mono text-xs text-neutral-500">
                              {p.slug}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {p.schedule.enabled ? (
                              <span className="font-mono text-xs text-neutral-700">
                                {p.schedule.cron}{" "}
                                <span className="text-neutral-500">
                                  {p.schedule.timezone} · {p.schedule.mode}
                                </span>
                              </span>
                            ) : (
                              <Badge variant="neutral" size="sm" pill>
                                {t("operations.projects.manualOnly")}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <StatusChip status={p.state.last_status} />
                            <span className="ml-2 text-xs text-neutral-500">
                              {formatTs(p.state.last_run_at)}
                            </span>
                            {p.state.consecutive_failures > 0 && (
                              <Badge
                                variant="error"
                                size="sm"
                                className="ml-2"
                              >
                                ×{p.state.consecutive_failures}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-neutral-500">
                            {p.schedule.enabled
                              ? formatTs(p.state.next_run_at)
                              : "—"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            <Button
                              ghost
                              size="xs"
                              onClick={() => void handleRunNow(p.slug, false)}
                              disabled={busySlug !== null}
                              loading={busySlug === p.slug}
                              leftIcon={<Zap size={12} />}
                            >
                              {t("operations.runNow")}
                            </Button>
                            {inFlight && (
                              <Button
                                variant="danger"
                                ghost
                                size="xs"
                                onClick={() => void handleRunNow(p.slug, true)}
                                disabled={busySlug !== null}
                                leftIcon={<Skull size={12} />}
                                title={t("operations.forceReapTitle")}
                              >
                                {t("operations.forceReap")}
                              </Button>
                            )}
                            <Link
                              to={`/projects/${encodeURIComponent(p.slug)}?tab=loader`}
                              className="ml-1 inline-flex items-center gap-0.5 text-xs text-primary hover:underline"
                            >
                              {t("operations.projects.hubLink")}
                              <ArrowUpRight size={11} />
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Corridas recientes cross-proyecto ────────────── */}
          <Card variant="bordered" noPadding>
            <header className="border-b border-neutral-200 bg-neutral-50 px-4 py-2">
              <span className="text-sm font-semibold text-neutral-800">
                {t("operations.jobs.title")}
              </span>
              <span className="ml-2 text-xs text-neutral-500">
                {t("operations.jobs.hint")}
              </span>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-neutral-200 bg-neutral-50/60">
                  <tr>
                    <Th>{t("operations.projects.project")}</Th>
                    <Th>{t("operations.jobs.status")}</Th>
                    <Th>{t("operations.jobs.phase")}</Th>
                    <Th>{t("operations.jobs.started")}</Th>
                    <Th>{t("operations.jobs.duration")}</Th>
                    <Th>{t("operations.jobs.recon")}</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {data.recent_jobs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-6 text-center text-neutral-500"
                      >
                        {t("operations.jobs.empty")}
                      </td>
                    </tr>
                  ) : (
                    data.recent_jobs.map((j) => (
                      <tr key={j.id} className="hover:bg-neutral-50">
                        <td className="px-4 py-2.5">
                          <Link
                            to={`/projects/${encodeURIComponent(j.project_slug)}?tab=loader`}
                            className="text-neutral-900 hover:text-primary"
                          >
                            {projectLabel(j.project_slug)}
                          </Link>
                          <span className="ml-2 text-xs text-neutral-500">
                            {j.mode}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusChip status={j.status} />
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-neutral-500">
                          {j.status === "running" && j.progress_json
                            ? `${j.progress_json.phase}${
                                j.progress_json.view
                                  ? ` · ${j.progress_json.view}`
                                  : ""
                              }`
                            : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-neutral-500">
                          {formatTs(j.started_at)}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-neutral-500">
                          {formatDuration(j)}
                        </td>
                        <td className="px-4 py-2.5">
                          <ReconChip job={j} />
                          {j.status === "failed" && j.error_summary && (
                            <span
                              className="ml-2 inline-block max-w-[260px] truncate align-middle text-xs text-error-dark"
                              title={j.error_summary}
                            >
                              {j.error_summary}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Recent deliveries (track B2) ──────────────────── */}
          <Card variant="bordered" className="overflow-hidden">
            <header className="flex items-center border-b border-neutral-200 px-4 py-3">
              <span className="text-sm font-semibold text-neutral-900">
                {t("delivery.recent.title")}
              </span>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-neutral-200 bg-neutral-50/60">
                  <tr>
                    <Th>{t("delivery.field.name")}</Th>
                    <Th>{t("operations.projects.project")}</Th>
                    <Th>{t("delivery.field.channel")}</Th>
                    <Th>{t("delivery.field.locale")}</Th>
                    <Th>{t("operations.jobs.status")}</Th>
                    <Th>{t("delivery.col.nextRun")}</Th>
                    <Th>{""}</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {deliveries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-neutral-500"
                      >
                        {t("delivery.recent.empty")}
                      </td>
                    </tr>
                  ) : (
                    deliveries.map((j) => (
                      <tr key={j.id} className="hover:bg-neutral-50/60">
                        <td className="px-4 py-2.5 text-neutral-800">
                          {j.subscription_name ?? "—"}
                        </td>
                        <td className="px-4 py-2.5 text-neutral-600">
                          {projectLabel(j.project_slug)}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant="neutral" size="sm" pill>
                            {t(`delivery.channel.${j.channel}`)}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-neutral-600">
                          {j.locale}
                        </td>
                        <td
                          className="px-4 py-2.5"
                          title={
                            j.status === "sent"
                              ? t("delivery.sentMeaning")
                              : undefined
                          }
                        >
                          <Badge
                            variant={
                              j.status === "sent"
                                ? "success"
                                : j.status === "failed" || j.status === "dead"
                                  ? "error"
                                  : j.status === "running"
                                    ? "info"
                                    : "warning"
                            }
                            size="sm"
                            pill
                          >
                            {t(`delivery.status.${j.status}`)}
                          </Badge>
                          <span className="ml-1 text-xs text-neutral-500">
                            {j.attempts}/{j.max_attempts}
                          </span>
                          {j.error_summary && (
                            <div className="mt-0.5 text-xs text-error-dark">
                              [{j.error_kind}] {j.error_summary}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-neutral-600">
                          {formatTs(j.scheduled_for)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {(j.status === "failed" || j.status === "dead") && (
                            <Button
                              ghost
                              size="sm"
                              disabled={retryingJobId === j.id}
                              onClick={() => void handleRetryDelivery(j.id)}
                              leftIcon={<RefreshCw size={14} />}
                            >
                              {t("delivery.action.retry")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Ambitos de datos (Plan J J6) ──────────────────── */}
          {/* Its own aggregate call, gated `operations` like this page
              — every other scope endpoint is gated `manage_users`, so
              reading them here would 403 for exactly the operator this
              cockpit is built for. It renders nothing until it has
              content, so a tenant with no scope sees no empty card. */}
          <ScopesPanel />
        </div>
      )}
    </PageContainer>
  );
}

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <th
      className={`px-4 py-2.5 text-left text-xs font-medium text-neutral-500 ${className}`}
    >
      {children}
    </th>
  );
}

/** Job states the loader writes today. Anything else renders RAW. */
export const KNOWN_JOB_STATUS = [
  "pending",
  "queued",
  "running",
  "completed",
  "failed",
  "skipped",
] as const;

export function StatusChip({ status }: { status: string | null }): JSX.Element {
  const { t } = useT();
  if (!status) return <span className="text-xs text-neutral-500">—</span>;
  const known = (KNOWN_JOB_STATUS as readonly string[]).includes(status);
  const variant =
    status === "completed"
      ? "success"
      : status === "failed"
        ? "error"
        : status === "running"
          ? "info"
          : "neutral";
  // A state the front does not know is shown as the backend wrote it, in
  // monospace, with a tooltip saying the label is missing — never
  // prettified into something that reads like a real state.
  return known ? (
    <Badge variant={variant} size="sm" pill>
      {t(`operations.jobStatus.${status}`)}
    </Badge>
  ) : (
    <Badge variant="warning" size="sm" pill title={t("common.missingLabel")}>
      <span className="font-mono">{status}</span>
    </Badge>
  );
}

/**
 * Compact reconciliation chip — same two `result_json.reconciliation`
 * shapes the hub badge reads (nested `{gate, portal, portal_warning}`
 * or the flat gate summary on gate-blocked failures). The full
 * per-element drawer stays in the hub.
 */
export function ReconChip({ job }: { job: LoaderJob }): JSX.Element {
  const { t } = useT();
  const recon = (job.result_json?.reconciliation ?? null) as Record<
    string,
    unknown
  > | null;
  if (!recon) return <span className="text-xs text-neutral-500">—</span>;

  const asCount = (o: unknown, k: string): number => {
    const v = (o as Record<string, unknown> | null)?.[k];
    return typeof v === "number" ? v : 0;
  };
  const gate = ("gate" in recon ? recon.gate : recon) as Record<
    string,
    unknown
  >;
  const failed = asCount(gate, "failed") + asCount(gate, "coverage_gaps");
  const passed = asCount(gate, "passed");
  const portalWarning = recon.portal_warning === true;

  return (
    <span className="inline-flex items-center gap-1 align-middle">
      {failed > 0 ? (
        <Badge variant="error" size="sm">
          ✗ {failed}
        </Badge>
      ) : passed === 0 ? (
        // #264 — nothing compared is not a pass: no "✓ 0".
        <Badge
          variant="neutral"
          size="sm"
          title={t("loader.recon.noChecksTitle")}
        >
          {t("loader.recon.noChecks")}
        </Badge>
      ) : (
        <Badge variant="success" size="sm">
          ✓ {passed}
        </Badge>
      )}
      {portalWarning && (
        <Badge variant="warning" size="sm">
          ⚠ portal
        </Badge>
      )}
    </span>
  );
}

function formatTs(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(j: LoaderJob): string {
  if (!j.started_at) return "—";
  const start = new Date(
    j.started_at.endsWith("Z") ? j.started_at : `${j.started_at}Z`,
  ).getTime();
  const end = j.finished_at
    ? new Date(
        j.finished_at.endsWith("Z") ? j.finished_at : `${j.finished_at}Z`,
      ).getTime()
    : Date.now();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return "—";
  const s = Math.round((end - start) / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
